import { useContext } from 'react';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { useGetCaseDetails } from '../../api/casedetails';
import UserProfileContext from '../../context/userprofilecontext.js';
import { formatDate, formatCurrency, formatMonthShort, formatOwnershipAllocation } from '../../utils/formatters.js';
import { formatAccountDisplayName } from '../../utils/scanReportUtils';
import {
    addFootersToAllPages,
    addFiltersDescription,
    addPageIfNeeded,
    normalizeMargin,
    sanitizeForHelvetica,
    standardPDFStyles,
} from '../shared/PDFCommon.js';
import { processData, getMonthCoverageData } from './report-statement-quality-heatmap.js';
import { orderQualityAccounts, qualityGroupLabel, QUALITY_GROUP_SEPARATOR_PDF } from './report-statement-quality-order.js';

/*
 * PDF export for the Quality & Completeness report.
 *
 * This renders from the report payload, not from the DOM, which is the point:
 * on screen each account's detail table sits inside a
 * `<Collapse unmountOnExit>`, so a browser print silently omits every account
 * the user has not expanded. On a completeness report that produces a document
 * that looks complete and is not. Rendering from data also lets the coverage
 * matrix paginate across months instead of being clipped at the page edge.
 *
 * Landscape is a deliberate departure from the portrait house default (MBS,
 * DRFA, Transactions): the matrix and the detail table both need the width.
 */

// Landscape letter is 792 x 612pt.
const MARGIN = 45;
const GUTTER_W = 160;      // account-name column; "*1111 Chase Checking" ~90pt at 6.5pt Times
const CELL_W = 14;         // "Jan" is ~11pt at 5.5pt Times, so the label still fits
const ROW_H = 14;
const GROUP_H = 13;
const AXIS_H = 22;         // year band + month band
const FOOTER_RESERVE = 40; // addFootersToAllPages draws 30pt from the bottom
const ACCOUNT_GAP = 16;    // vertical gap between one account's table and the next

// Hand-mapped from report-statement-quality-heatmap.module.css. The PDF modules
// use no design tokens, so these must be updated together with the CSS if the
// on-screen palette changes.
const STATUS_RGB = {
    OK: [29, 155, 94],            // --color-success-500
    HAS_WARNINGS: [217, 119, 6],  // --color-warning-500
    MISSING: [255, 255, 255],     // --surface-primary
    DEFAULT: [220, 38, 38],       // --color-error-500, balance mismatches
};

const STATUS_LABEL = {
    OK: 'OK',
    HAS_WARNINGS: 'Warnings',
    MISSING: 'MISSING',
    BALANCE_TRANSACTION_MISMATCH: 'Balance/txn mismatch',
    BALANCE_CONTINUITY_MISMATCH: 'Balance continuity',
};

const statusRgb = (qualityStatus) => STATUS_RGB[qualityStatus] || STATUS_RGB.DEFAULT;

const statusLabel = (statement) => {
    if (statement.missing) return 'MISSING';
    return STATUS_LABEL[statement.quality_status] || statement.quality_status || 'Needs review';
};

// Balances are legitimately null on investment statements; render those as a
// dash rather than "$0.00", which would assert a fact the data does not carry.
const money = (value) => (value === null || value === undefined || value === '' ? '-' : `$${formatCurrency(value)}`);

const text = (value) => sanitizeForHelvetica(value ?? '');

const documentLabel = (statement) => {
    if (!statement.document_name) return 'No Document';
    let name = statement.document_name;
    try {
        name = decodeURIComponent(name);
    } catch (err) {
        // A malformed escape sequence should not lose the row.
    }
    return sanitizeForHelvetica(name);
};

// Rows in canonical order (split into Personal / per-entity sections when any
// account is entity-titled), with a group-header row wherever the group
// changes — the same shape the heatmap builds for the screen.
const buildLayoutRows = (data) => {
    const rows = [];
    let prevGroupKey = null;
    orderQualityAccounts(data).forEach((entry) => {
        const { accountId, group, groupKey } = entry;
        if (group && groupKey !== prevGroupKey) {
            rows.push({ type: 'header', label: sanitizeForHelvetica(qualityGroupLabel(entry, QUALITY_GROUP_SEPARATOR_PDF)) });
            prevGroupKey = groupKey;
        }
        rows.push({ type: 'account', accountId });
    });
    return rows;
};

const accountName = (data, accountId) => sanitizeForHelvetica(
    formatAccountDisplayName(data[accountId].bank_account) || `Account ${accountId}`
);

const addTitle = (doc, pageWidth, yPosition, { currentCase, caseDetails, accountCount, monthCount }) => {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(text(currentCase.caseName || 'Petitioner v. Respondent'), pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 16;

    if (caseDetails?.courtJurisdiction) {
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        const courtInfo = `${caseDetails.courtJurisdiction}${caseDetails.jurisdictionState ? `, ${caseDetails.jurisdictionState}` : ''}`;
        doc.text(text(courtInfo), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 14;
    }

    if (caseDetails?.caseNumber) {
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        doc.text(`Civil Action File No.: ${text(caseDetails.caseNumber)}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 14;
    }

    yPosition += 12;
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Quality & Completeness', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 16;

    if (caseDetails?.marriageDate) {
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        doc.text(`Date of Marriage: ${formatDate(new Date(caseDetails.marriageDate))}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 14;
    }

    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    if (currentCase.caseName) {
        doc.text(`For litigation purposes only in the matter of ${text(currentCase.caseName)}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 16;
    }

    doc.text(`Accounts: ${accountCount}    Months covered: ${monthCount}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 20;

    return yPosition;
};

// Color key. Deliberately swatches-and-words: check/cross glyphs are outside
// printable ASCII and would be stripped by sanitizeForHelvetica.
const addLegend = (doc, margin, yPosition) => {
    // Wording and entries must match the on-screen key exactly — on a forensic
    // document the export and the screen saying different things about the same
    // color is a defect, not a cosmetic difference.
    const entries = [
        [STATUS_RGB.OK, 'OK', false],
        [STATUS_RGB.HAS_WARNINGS, 'Warning', false],
        [STATUS_RGB.DEFAULT, 'Needs review', false],
        [STATUS_RGB.MISSING, 'Missing', false],
        [STATUS_RGB.OK, 'Partial month', true],
    ];

    const swatch = 8;
    // Fixed slots rather than measured text: jsPDF v4 has no getTextWidth, and
    // an evenly divided row keeps the key aligned regardless of label length.
    const contentWidth = doc.internal.pageSize.getWidth() - margin.left - margin.right;
    const slot = contentWidth / entries.length;

    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setLineWidth(standardPDFStyles.table.body.default.lineWidth);
    doc.setDrawColor(...standardPDFStyles.table.body.default.lineColor);

    entries.forEach(([rgb, label, isPartial], index) => {
        const x = margin.left + (index * slot);
        if (isPartial) {
            // Half filled, mirroring how a partly covered month is drawn.
            doc.setFillColor(...STATUS_RGB.MISSING);
            doc.rect(x, yPosition, swatch, swatch, 'F');
            doc.setFillColor(...rgb);
            doc.rect(x, yPosition, swatch / 2, swatch, 'F');
        } else {
            doc.setFillColor(...rgb);
            doc.rect(x, yPosition, swatch, swatch, 'F');
        }
        doc.rect(x, yPosition, swatch, swatch);
        doc.setTextColor(0, 0, 0);
        doc.text(label, x + swatch + 4, yPosition + swatch - 1);
    });

    return yPosition + swatch + 12;
};

// The year/month bands above a chunk of month columns. Redrawn at the top of
// every page the matrix spills onto, which is what makes a paginated matrix
// readable.
const addAxisHeader = (doc, margin, yPosition, monthChunk) => {
    const gridLeft = margin.left + GUTTER_W;

    // Year band, centered over each year's run of months within this chunk.
    const years = [];
    monthChunk.forEach((month, index) => {
        const year = month.split('-')[0];
        const last = years[years.length - 1];
        if (last && last.year === year) last.end = index;
        else years.push({ year, start: index, end: index });
    });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    years.forEach(({ year, start, end }) => {
        const centerX = gridLeft + (start * CELL_W) + (((end - start + 1) * CELL_W) / 2);
        doc.text(year, centerX, yPosition + 8, { align: 'center' });
    });

    doc.setFontSize(5.5);
    doc.setFont(undefined, 'normal');
    monthChunk.forEach((month, index) => {
        doc.text(
            formatMonthShort(month),
            gridLeft + (index * CELL_W) + (CELL_W / 2),
            yPosition + AXIS_H - 4,
            { align: 'center' }
        );
    });

    return yPosition + AXIS_H;
};

/*
 * The coverage matrix, paginated in both directions: an outer pass over
 * chunks of months that fit the page width, and within each chunk a vertical
 * break when the rows run out of room. The account gutter and the month axis
 * are repeated on every page.
 */
/*
 * Splits a chunk's rows across pages, then pulls rows back from the
 * second-to-last page if the last one would be left with a single stranded
 * account. A lone row on an otherwise blank page reads as an error.
 */
const MIN_TAIL_ROWS = 2;

export const paginateMatrixRows = (rows, firstPageHeight, laterPageHeight) => {
    const pages = [];
    let current = [];
    let used = 0;
    let available = firstPageHeight;

    rows.forEach((row, i) => {
        const height = row.type === 'header' ? GROUP_H : ROW_H;
        // A group band needs room for its first account too, so a page never
        // ends on a band whose accounts start on the next page.
        const needed = row.type === 'header' && rows[i + 1]?.type === 'account' ? height + ROW_H : height;
        if (used + needed > available && current.length > 0) {
            pages.push(current);
            current = [];
            used = 0;
            available = laterPageHeight;
        }
        current.push(row);
        used += height;
    });
    if (current.length > 0) pages.push(current);

    const accountsOn = (page) => page.filter((r) => r.type === 'account').length;
    if (pages.length > 1) {
        const last = pages[pages.length - 1];
        const prev = pages[pages.length - 2];
        // Moving rows forward only ever shortens `prev` and fills space `last`
        // already has, so this cannot overflow either page.
        while (accountsOn(last) < MIN_TAIL_ROWS && accountsOn(prev) > MIN_TAIL_ROWS) {
            last.unshift(prev.pop());
        }
        // The balancing above can leave `prev` ending on a band; carry it over
        // with its accounts.
        while (prev.length > 1 && prev[prev.length - 1].type === 'header') {
            last.unshift(prev.pop());
        }
    }

    return pages;
};

const addCoverageMatrix = (doc, margin, startY, data, months, layoutRows) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = doc.internal.pageSize.getWidth() - margin.left - margin.right;
    const monthsPerPage = Math.max(1, Math.floor((contentWidth - GUTTER_W) / CELL_W));
    const bottomLimit = pageHeight - margin.bottom - FOOTER_RESERVE;

    const drawGroupBand = (label, y, width, suffix) => {
        doc.setFillColor(...standardPDFStyles.table.header.default.fillColor);
        doc.rect(margin.left, y, width, GROUP_H, 'F');
        doc.setTextColor(...standardPDFStyles.table.header.default.textColor);
        doc.setFontSize(7);
        doc.setFont(undefined, 'bold');
        doc.text(text(label + suffix), margin.left + 3, y + GROUP_H - 4);
        return y + GROUP_H;
    };

    let yPosition = startY;

    for (let chunkStart = 0; chunkStart < months.length; chunkStart += monthsPerPage) {
        const monthChunk = months.slice(chunkStart, chunkStart + monthsPerPage);
        const chunkWidth = GUTTER_W + (monthChunk.length * CELL_W);

        const range = `${formatMonthShort(monthChunk[0])} ${monthChunk[0].split('-')[0]} - `
            + `${formatMonthShort(monthChunk[monthChunk.length - 1])} ${monthChunk[monthChunk.length - 1].split('-')[0]}`;

        // Every page of a chunk carries the same masthead: which months it
        // covers, then the axis. A continuation page without the date range
        // shows month names with nothing to anchor them to.
        const drawChunkMasthead = (y) => {
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(`Statement coverage: ${range}`, margin.left, y + 8);
            return addAxisHeader(doc, margin, y + 16, monthChunk);
        };

        if (chunkStart > 0) {
            doc.addPage();
            yPosition = margin.top;
        }

        const firstPageHeight = bottomLimit - (yPosition + 16 + AXIS_H);
        const laterPageHeight = bottomLimit - (margin.top + 16 + AXIS_H);
        const pages = paginateMatrixRows(layoutRows, firstPageHeight, laterPageHeight);

        let currentGroup = null;
        for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
            const pageRows = pages[pageIndex];
            if (pageIndex > 0) {
                doc.addPage();
                yPosition = margin.top;
            }
            yPosition = drawChunkMasthead(yPosition);

            // Carry the group forward. Without this a page that opens mid-group
            // shows accounts with nothing saying which type they are — on a
            // forensic report that is unreadable, not merely untidy.
            if (pageIndex > 0 && currentGroup && pageRows[0].type !== 'header') {
                yPosition = drawGroupBand(currentGroup, yPosition, chunkWidth, ' (continued)');
            }

            for (const row of pageRows) {
                if (row.type === 'header') {
                    currentGroup = row.label;
                    yPosition = drawGroupBand(row.label, yPosition, chunkWidth, '');
                    continue;
                }

                doc.setTextColor(0, 0, 0);
                doc.setFontSize(6.5);
                doc.setFont(undefined, 'normal');
                doc.text(
                    accountName(data, row.accountId),
                    margin.left + GUTTER_W - 4,
                    yPosition + ROW_H - 4,
                    { align: 'right' }
                );

                doc.setLineWidth(standardPDFStyles.table.body.default.lineWidth);
                doc.setDrawColor(...standardPDFStyles.table.body.default.lineColor);

                for (let index = 0; index < monthChunk.length; index += 1) {
                    const x = margin.left + GUTTER_W + (index * CELL_W);

                    // Uncovered cell: white, so a gap reads as a gap on paper.
                    doc.setFillColor(...STATUS_RGB.MISSING);
                    doc.rect(x, yPosition, CELL_W, ROW_H, 'F');

                    const segments = getMonthCoverageData(data, row.accountId, monthChunk[index]);
                    for (const segment of segments) {
                        const segW = (segment.endFraction - segment.startFraction) * CELL_W;
                        if (segW <= 0) continue;
                        doc.setFillColor(...statusRgb(segment.quality_status));
                        doc.rect(x + (segment.startFraction * CELL_W), yPosition, segW, ROW_H, 'F');
                    }

                    doc.rect(x, yPosition, CELL_W, ROW_H);
                }

                yPosition += ROW_H;
            }
        }
    }

    return yPosition;
};

const DETAIL_COLUMNS = [
    { header: 'Document', dataKey: 'document', columnStyle: { cellWidth: 175, halign: 'left' } },
    { header: 'Start Date', dataKey: 'startDate', columnStyle: { cellWidth: 62, halign: 'right' } },
    { header: 'End Date', dataKey: 'endDate', columnStyle: { cellWidth: 62, halign: 'right' } },
    { header: 'Starting Balance', dataKey: 'startBalance', columnStyle: { cellWidth: 78, halign: 'right' } },
    { header: 'Ending Balance', dataKey: 'endBalance', columnStyle: { cellWidth: 78, halign: 'right' } },
    { header: 'Expected Txn Total', dataKey: 'expectedTotal', columnStyle: { cellWidth: 80, halign: 'right' } },
    { header: 'Actual Txn Total', dataKey: 'actualTotal', columnStyle: { cellWidth: 80, halign: 'right' } },
    { header: 'Status', dataKey: 'status', columnStyle: { cellWidth: 85, halign: 'left' } },
];

const detailRow = (statement) => {
    if (statement.missing) {
        // Gaps are the point of a completeness report — they get a row, not a filter.
        return {
            document: 'MISSING',
            startDate: text(statement.expected_begin_date),
            endDate: text(statement.expected_end_date),
            startBalance: '-',
            endBalance: '-',
            expectedTotal: '-',
            actualTotal: '-',
            status: 'MISSING',
            _missing: true,
        };
    }

    return {
        document: documentLabel(statement),
        startDate: statement.begin_date ? formatDate(statement.begin_date) : '-',
        endDate: statement.end_date ? formatDate(statement.end_date) : '-',
        startBalance: money(statement.begin_balance),
        endBalance: money(statement.end_balance),
        expectedTotal: money(statement.expected_transaction_total),
        actualTotal: money(statement.transaction_total),
        status: statusLabel(statement),
        _missing: false,
    };
};

/*
 * One table per account, every account, in canonical order. Collapse state is
 * never consulted — that is the bug this export exists to avoid.
 */
const addDetailTables = (doc, margin, startY, data, layoutRows) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPosition = startY;

    doc.addPage();
    yPosition = margin.top;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Quality & Completeness - Detail', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 22;

    layoutRows.forEach((row) => {
        if (row.type === 'header') {
            // Keep the band with its first account: accounts start a new page
            // past addPageIfNeeded's threshold, so check where the account
            // would begin, not just where the band would.
            const firstAccountY = yPosition + 16 + ACCOUNT_GAP;
            if (addPageIfNeeded(doc, margin, firstAccountY) !== firstAccountY) {
                yPosition = margin.top; // a page was added
            }
            doc.setFillColor(...standardPDFStyles.table.header.default.fillColor);
            doc.rect(margin.left, yPosition, pageWidth - margin.left - margin.right, 16, 'F');
            doc.setTextColor(...standardPDFStyles.table.header.default.textColor);
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(text(row.label), margin.left + 4, yPosition + 11);
            // 16pt band height + the same gap used between accounts, so a
            // group header doesn't crowd the first account under it.
            yPosition += 16 + ACCOUNT_GAP;
            return;
        }

        const statements = data[row.accountId].combined_statements || [];

        yPosition = addPageIfNeeded(doc, margin, yPosition);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        // Title is account-level, not per-statement, so it rides the heading
        // line rather than becoming a DETAIL_COLUMNS entry that would repeat
        // the same value on every statement row.
        const titleText = sanitizeForHelvetica(formatOwnershipAllocation(data[row.accountId].title, { abbreviate: true }) || 'Untitled');
        // Plain ASCII separator, not an em-dash -- sanitizeForHelvetica exists
        // specifically because jsPDF's Helvetica metrics break on non-ASCII
        // characters like em-dashes (see its doc comment); a raw em-dash typed
        // straight into this template would reintroduce exactly that bug.
        doc.text(`${accountName(data, row.accountId)} - ${titleText}`, margin.left, yPosition);
        yPosition += 6;

        autoTable(doc, {
            columns: DETAIL_COLUMNS,
            body: statements.map(detailRow),
            startY: yPosition,
            styles: {
                overflow: 'linebreak',
                cellWidth: 'auto',
                font: 'times',
                fontSize: 7,
                valign: 'middle',
                lineColor: standardPDFStyles.table.body.default.lineColor,
                lineWidth: standardPDFStyles.table.body.default.lineWidth,
            },
            headStyles: {
                fillColor: standardPDFStyles.table.header.default.fillColor,
                textColor: standardPDFStyles.table.header.default.textColor,
                halign: 'center',
            },
            columnStyles: DETAIL_COLUMNS.reduce((acc, col, index) => {
                acc[index] = col.columnStyle || {};
                return acc;
            }, {}),
            margin: { top: margin.top, left: margin.left, right: margin.right, bottom: margin.bottom },
            didParseCell: (hookData) => {
                if (hookData.section === 'body' && hookData.row.raw?._missing) {
                    hookData.cell.styles.textColor = STATUS_RGB.DEFAULT;
                    hookData.cell.styles.fontStyle = 'bold';
                }
            },
        });

        // Fallback keeps the run going if a table ever takes autotable's
        // horizontal-page-break path, which leaves lastAutoTable unset.
        yPosition = (doc.lastAutoTable?.finalY ?? yPosition + 40) + ACCOUNT_GAP;
    });

    return yPosition;
};

/*
 * Builds the document and returns it, without saving. Kept free of React so
 * the PDF can be generated headlessly — a test can write a real file and have
 * it opened, rather than only asserting that doc.save() was called.
 *
 * Returns null when there is nothing to print.
 */
export const buildStatementQualityPDF = ({
    data,
    currentCase = {},
    currentOrg = {},
    caseDetails = null,
    displayOptions = { isDraft: true },
    visibleYears = null,
    filtersDescription = null,
}) => {
    if (!data || Object.keys(data).length === 0) return null;

    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'letter',
    });
    doc.setFont('Times', 'normal');

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = normalizeMargin(MARGIN);

    const { months } = processData(data, visibleYears);
    const layoutRows = buildLayoutRows(data);
    const accountCount = layoutRows.filter((r) => r.type === 'account').length;

    let yPosition = margin.top;
    yPosition = addTitle(doc, pageWidth, yPosition, {
        currentCase,
        caseDetails,
        accountCount,
        monthCount: months.length,
    });
    // Stamp the active filters onto the document. A partial export that
    // does not say it is partial is the failure mode this guards against.
    if (filtersDescription && filtersDescription.length > 0) {
        yPosition = addFiltersDescription(doc, filtersDescription, MARGIN, yPosition);
    }
    yPosition = addLegend(doc, margin, yPosition);
    yPosition = addCoverageMatrix(doc, margin, yPosition, data, months, layoutRows);
    addDetailTables(doc, margin, yPosition, data, layoutRows);

    // Last, so every page exists and the page count is right.
    addFootersToAllPages(doc, currentOrg, displayOptions || {}, MARGIN);

    return doc;
};

export const statementQualityFilename = (currentCase = {}) => {
    const caseSlug = currentCase.caseName
        ? currentCase.caseName.replace(/[^a-zA-Z0-9]/g, '_')
        : 'Document';
    return `Quality_And_Completeness_${caseSlug}_${new Date().toISOString().split('T')[0]}.pdf`;
};

/*
 * Plain function returning a closure, matching usePrintMBSPDF /
 * usePDFPrintTransactions. It calls React hooks, so it must be invoked at the
 * top level of a component and ABOVE any early return.
 */
const usePrintStatementQualityPDF = (data, displayOptions = { isDraft: true }, filtersDescription = null, visibleYears = null) => {
    const { data: caseDetails } = useGetCaseDetails();
    const userProfileContext = useContext(UserProfileContext);
    const currentCase = userProfileContext?.userProfile?.currentCase || {};
    const currentOrg = userProfileContext?.userProfile?.currentOrg || {};

    return () => {
        const doc = buildStatementQualityPDF({
            data, currentCase, currentOrg, caseDetails, displayOptions, filtersDescription, visibleYears,
        });
        if (!doc) return;
        doc.save(statementQualityFilename(currentCase));
    };
};

export default usePrintStatementQualityPDF;
